import React, { useState, useRef } from 'react';
import { CHANNEL_LABELS, TARGET_AUDIENCE_OPTIONS, CATEGORY_USE_CASES, FEATURES } from '@/lib/constants';
import type { Product } from '@/lib/types';
import { DB, authFetch } from '@/lib/supabase';
import { EditableFieldRow } from './EditableFieldRow';
import { Badge } from './Badge';

interface ApprovedImage {
  id: string;
  image_url: string;
  image_type: string;
  image_style?: 'sketch' | 'realism';
  slot: number;
  approved_at: string;
}

interface ExpandedProductPanelProps {
  product: Product;
  isLast: boolean;
  onSave?: (product: Product) => Promise<void>;
  onDelete?: (productId: string) => void;
  locked?: boolean;
  /** Child variant products (only passed when FEATURES.variants is on and product is a parent) */
  childProducts?: Product[];
}

const PXM_DEBUG = import.meta.env.DEV;
const log = (...args: unknown[]) => { if (PXM_DEBUG) console.log("[PXM]", ...args); };

// Upload image to Supabase Storage via Netlify function, returns public URL
const uploadToStorage = async (dataUrl: string, folder: string, filename: string): Promise<string> => {
  try {
    const res = await authFetch("/api/upload-image", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ dataUrl, folder, filename }),
    });
    const data = await res.json() as { success?: boolean; url?: string; error?: string };
    if (data.success && data.url) return data.url;
    console.error("[uploadToStorage] Failed:", data.error);
    return dataUrl; // fallback to base64 if upload fails
  } catch (err) {
    console.error("[uploadToStorage] Error:", err);
    return dataUrl; // fallback to base64
  }
};

// ─── Variants Tab Content ─────────────────────────────────────
function VariantsTabContent({
  parentProduct,
  childProducts,
  onSave,
  onDelete,
  locked,
}: {
  parentProduct: Product;
  childProducts: Product[];
  onSave: (product: Product) => Promise<void>;
  onDelete: (productId: string) => void;
  locked?: boolean;
}) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newItemNumber, setNewItemNumber] = useState("");

  // Attach existing product search state
  const [showAttachSearch, setShowAttachSearch] = useState(false);
  const [attachQuery, setAttachQuery] = useState("");
  const [attachResults, setAttachResults] = useState<Product[]>([]);
  const [attachSearching, setAttachSearching] = useState(false);
  const attachTimer = useRef<NodeJS.Timeout | null>(null);

  const handleCreateVariant = async () => {
    if (!newLabel.trim()) return;
    const variant: Product = {
      title: parentProduct.title,
      category: parentProduct.category,
      dimensions: parentProduct.dimensions,
      weight: parentProduct.weight,
      material: parentProduct.material,
      packSize: parentProduct.packSize,
      targetAudience: parentProduct.targetAudience,
      useCases: parentProduct.useCases,
      differentiators: parentProduct.differentiators,
      whatsIncluded: parentProduct.whatsIncluded,
      sellingFeatures: parentProduct.sellingFeatures,
      backendKeywords: parentProduct.backendKeywords,
      competitorRefs: parentProduct.competitorRefs,
      programId: parentProduct.programId,
      parentProductId: parentProduct.id,
      variantLabel: newLabel.trim(),
      itemNumber: newItemNumber.trim() || `${parentProduct.itemNumber || ""}-${newLabel.trim().toUpperCase().replace(/\s+/g, "-")}`,
      completion: 0,
      copyOverrides: {},
    };
    await onSave(variant);
    setNewLabel("");
    setNewItemNumber("");
    setShowAddForm(false);
  };

  // Search for existing products to attach as variants
  const handleAttachSearch = (query: string) => {
    setAttachQuery(query);
    if (attachTimer.current) clearTimeout(attachTimer.current);
    if (!query.trim()) { setAttachResults([]); setAttachSearching(false); return; }
    setAttachSearching(true);
    attachTimer.current = setTimeout(async () => {
      const results = await DB.searchProducts(query.trim(), null, 20);
      // Filter out: this parent, existing children, and products that are already variants of another parent
      const childIds = new Set(childProducts.map(c => String(c.id)));
      const filtered = (results || []).filter(p =>
        String(p.id) !== String(parentProduct.id) &&
        !childIds.has(String(p.id)) &&
        !p.parentProductId
      );
      setAttachResults(filtered);
      setAttachSearching(false);
    }, 300);
  };

  // Attach an existing product as a variant — keeps all its data, just sets parentProductId
  const handleAttachProduct = async (product: Product) => {
    await onSave({
      ...product,
      parentProductId: parentProduct.id,
      variantLabel: product.variantLabel || product.title || "Variant",
      programId: parentProduct.programId,
    });
    // Remove from search results
    setAttachResults(prev => prev.filter(p => String(p.id) !== String(product.id)));
  };

  // Unlink a variant — clears parentProductId, makes it standalone
  const handleUnlinkVariant = async (child: Product) => {
    await onSave({
      ...child,
      parentProductId: null,
      variantLabel: undefined,
    });
  };

  return (
    <div className="flex-col gap-5">
      {/* Header */}
      <div className="flex-between">
        <div>
          <div className="ff-primary fw-700 uppercase ls-wide text-mid text-xs">
            Product Variants
          </div>
          <div className="ff-secondary text-light text-md mt-half">
            Variants inherit copy, SEO, and product info from the parent. Override any field per variant.
          </div>
        </div>
        {!locked && (
          <div className="flex gap-2">
            {!showAddForm && !showAttachSearch && (
              <>
                <button
                  onClick={() => setShowAttachSearch(true)}
                  className="bg-none no-border rounded ff-secondary text-sm fw-600 pointer text-slate pad-input-sm border-slate"
                >
                  + Attach Existing
                </button>
                <button
                  onClick={() => setShowAddForm(true)}
                  className="bg-slate text-white no-border rounded ff-secondary text-sm fw-600 pointer pad-input-sm"
                >
                  + New Variant
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Add variant form */}
      {showAddForm && !locked && (
        <div className="card-section p-7 border-slate">
          <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">
            New Variant
          </div>
          <div className="flex gap-3 mb-3">
            <div className="flex-1">
              <div className="ff-secondary fw-600 text-mid mb-1 text-sm">Variant Label *</div>
              <input
                type="text"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder='e.g. "Blue", "12-Pack", "36×24"'
                className="w-full rounded text-base ff-secondary text-dark bg-card outline-none pad-md border-slate-25 box-border"
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateVariant(); }}
                autoFocus
              />
            </div>
            <div className="col-180">
              <div className="ff-secondary fw-600 text-mid mb-1 text-sm">Item Number (optional)</div>
              <input
                type="text"
                value={newItemNumber}
                onChange={(e) => setNewItemNumber(e.target.value)}
                placeholder="Auto-generated if blank"
                className="w-full rounded text-base ff-secondary text-dark bg-card outline-none pad-md border-slate-25 box-border"
                onKeyDown={(e) => { if (e.key === "Enter") handleCreateVariant(); }}
              />
            </div>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateVariant}
              disabled={!newLabel.trim()}
              className={`bg-slate text-white no-border rounded ff-secondary text-sm fw-600 pointer pad-input-sm ${newLabel.trim() ? '' : 'opacity-50'}`}
            >
              Create Variant
            </button>
            <button
              onClick={() => { setShowAddForm(false); setNewLabel(""); setNewItemNumber(""); }}
              className="bg-none no-border rounded ff-secondary text-sm fw-600 pointer text-mid pad-input-sm border"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Attach existing product search */}
      {showAttachSearch && !locked && (
        <div className="card-section p-7 border-slate">
          <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">
            Attach Existing Product
          </div>
          <div className="ff-secondary text-light mb-3 text-md">
            Search for a product by name or item number. It will be linked as a variant and keep all its existing data.
          </div>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={attachQuery}
              onChange={(e) => handleAttachSearch(e.target.value)}
              placeholder="Search by product name or item number..."
              className="flex-1 rounded text-base ff-secondary text-dark bg-card outline-none pad-md border-slate-25 box-border"
              autoFocus
            />
            <button
              onClick={() => { setShowAttachSearch(false); setAttachQuery(""); setAttachResults([]); }}
              className="bg-none no-border rounded ff-secondary text-sm fw-600 pointer text-mid pad-input-sm border"
            >
              Cancel
            </button>
          </div>
          {attachSearching && (
            <div className="ff-secondary text-light text-md py-3">Searching...</div>
          )}
          {!attachSearching && attachQuery.trim() && attachResults.length === 0 && (
            <div className="ff-secondary text-light text-md py-3">No matching products found.</div>
          )}
          {attachResults.length > 0 && (
            <div className="flex-col gap-1 max-h-240 overflow-y-auto">
              {attachResults.map((p) => (
                <div
                  key={p.id}
                  className="flex items-center gap-3 rounded bg-bg pointer pad-md border hover-border-slate"
                  onClick={() => handleAttachProduct(p)}
                >
                  <div className="col-32">
                    <div className="thumb size-28 text-xs">
                      {p.heroImage ? <img src={p.heroImage} alt="" /> : "◻"}
                    </div>
                  </div>
                  <span className="fw-600 ff-secondary text-slate text-sm col-90">
                    {p.itemNumber || "—"}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="fw-500 ff-secondary text-dark truncate text-base">
                      {p.title || "Untitled"}
                    </div>
                  </div>
                  <span className="ff-secondary fw-600 text-slate text-sm">Attach →</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Variant list */}
      {childProducts.length === 0 && !showAddForm && !showAttachSearch ? (
        <div className="flex-col items-center gap-2 py-9">
          <span className="text-5xl text-light">{"◇"}</span>
          <div className="ff-secondary fw-600 text-mid text-lg">No variants yet</div>
          <div className="ff-secondary text-light text-md">
            Add variants like color, size, or pack count. Each inherits the parent's content.
          </div>
        </div>
      ) : (
        <div className="flex-col gap-2">
          {childProducts.map((child) => {
            const copyChannels = Object.keys(child.approvedCopy || {});
            const overrideCount = Object.values(child.copyOverrides || {}).reduce(
              (sum, fields) => sum + (fields?.length || 0), 0
            );
            return (
              <div
                key={child.id}
                className="flex items-center gap-3 rounded bg-bg pad-base border"
              >
                {/* Thumbnail */}
                <div className="col-36">
                  <div className="thumb size-32 text-md">
                    {child.heroImage ? <img src={child.heroImage} alt="" /> : "◻"}
                  </div>
                </div>
                {/* Label pill */}
                <span
                  className="ff-secondary fw-600 rounded flex-none text-sm pad-chip-sm bg-raised text-charcoal"
                >
                  {child.variantLabel || "Variant"}
                </span>
                {/* Item number */}
                <span className="fw-600 ff-secondary text-slate text-md col-120">
                  {child.itemNumber || "—"}
                </span>
                {/* Title (truncated) */}
                <div className="flex-1 min-w-0">
                  <div className="fw-500 ff-secondary text-dark truncate text-base">
                    {child.title || parentProduct.title}
                  </div>
                </div>
                {/* Override count */}
                {overrideCount > 0 && (
                  <span className="ff-secondary fw-600 text-mid text-sm">
                    {overrideCount} override{overrideCount !== 1 ? "s" : ""}
                  </span>
                )}
                {/* Copy channels */}
                {copyChannels.length > 0 && (
                  <div className="flex gap-1">
                    {copyChannels.map((ch) => (
                      <span key={ch} className="ff-secondary fw-600 rounded text-xs pad-chip-xs bg-slate-10 text-mid">
                        {ch}
                      </span>
                    ))}
                  </div>
                )}
                {/* Unlink */}
                {!locked && (
                  <button
                    onClick={() => {
                      if (confirm(`Unlink "${child.variantLabel || child.title}" from this parent? It will become a standalone product.`)) {
                        handleUnlinkVariant(child);
                      }
                    }}
                    className="no-border bg-none pointer ff-secondary fw-600 text-sm pad-tight text-mid"
                    title="Unlink — make standalone"
                  >
                    Unlink
                  </button>
                )}
                {/* Delete */}
                {!locked && (
                  <button
                    onClick={() => {
                      if (confirm(`Delete variant "${child.variantLabel}"? This cannot be undone.`)) {
                        onDelete(String(child.id!));
                      }
                    }}
                    className="no-border bg-none pointer text-light text-xl p-0-4 lh-tight"
                    title="Delete variant"
                  >
                    {"×"}
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export const ExpandedProductPanel = React.memo(function ExpandedProductPanel({ product, isLast: _isLast, onSave, onDelete, locked = false, childProducts = [] }: ExpandedProductPanelProps) {
  const [activeTab, setActiveTab] = useState("media");
  const [heroDragOver, setHeroDragOver] = useState(false);
  const [heroZone, setHeroZone] = useState<"center" | "corner" | null>(null);
  const [detailDragOver, setDetailDragOver] = useState<number | null>(null);
  const [detailZone, setDetailZone] = useState<{ i: number; zone: "center" | "corner" } | null>(null);
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  const getZone = (e: React.MouseEvent<HTMLElement>): "center" | "corner" => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - r.left;
    const y = e.clientY - r.top;
    return (x > r.width - 40 && y > r.height - 40) ? "corner" : "center";
  };
  const [editData, setEditData] = useState<Product>({ ...product });
  const [detailLoading, setDetailLoading] = useState<Record<number, boolean>>({});
  const [heroLoading, setHeroLoading] = useState(false);
  const [detailSaveStatus, setDetailSaveStatus] = useState<'saving' | 'saved' | 'error' | null>(null);
  const [newFeature, setNewFeature] = useState("");
  const [keywordsGenerating, setKeywordsGenerating] = useState(false);
  const [approvedImages, setApprovedImages] = useState<ApprovedImage[]>([]);
  const [_approvedImagesLoading, setApprovedImagesLoading] = useState(false);
  const heroImageRef = React.useRef<HTMLInputElement>(null);
  const detailImageRefs = React.useRef<Record<number, HTMLInputElement>>({});
  const saveTimer = React.useRef<NodeJS.Timeout | null>(null);

  // Load approved images for this product
  React.useEffect(() => {
    const pid = product?.itemNumber || String(product?.id);
    if (!pid) return;
    setApprovedImagesLoading(true);
    DB.loadApprovedImages(pid).then(imgs => {
      setApprovedImages(imgs || []);
      setApprovedImagesLoading(false);
    });
  }, [product?.id, product?.itemNumber]);

  // Keep editData in sync if product prop changes externally (e.g. after DB roundtrip)
  React.useEffect(() => {
    setEditData(prev => {
      if (prev.id !== product.id) return { ...product };
      // Merge in any new fields from DB (like detailPhotos after save) without clobbering local edits
      const merged = { ...product };
      Object.keys(prev).forEach(k => { if (prev[k as keyof Product] !== undefined) (merged as Record<string, unknown>)[k] = (prev as Record<string, unknown>)[k]; });
      // But always take DB detailPhotos if local is empty and DB has data
      if ((!prev.detailPhotos || prev.detailPhotos.length === 0) && product.detailPhotos && product.detailPhotos.length > 0) {
        merged.detailPhotos = product.detailPhotos;
      }
      return merged;
    });
  }, [product]);

  const channelLabels = CHANNEL_LABELS;
  const approvedCopyData = product?.approvedCopy || {};
  const approvedChannels = Object.keys(approvedCopyData).filter(ch => approvedCopyData[ch]?.title || (approvedCopyData[ch]?.bullets || []).length > 0);
  const hasArtDirection = !!(product?.artDirection?.images?.length);
  const hasGeneratedImages = !!(
    approvedImages.length > 0 ||
    (product?.artDirection?.generatedImages && Object.keys(product.artDirection.generatedImages).some(style => Object.keys(product.artDirection!.generatedImages![style]).length > 0))
  );
  const isParentProduct = FEATURES.variants && !product.parentProductId;
  const tabs = [
    { key: "media", label: "Media" },
    { key: "info", label: "Product Info" },
    { key: "seo", label: "SEO Inputs" },
    ...approvedChannels.map(ch => ({ key: `copy_${ch}`, label: channelLabels[ch] || ch.charAt(0).toUpperCase() + ch.slice(1) })),
    ...(hasArtDirection ? [{ key: "visual-direction", label: "Visual Direction" }] : []),
    ...(hasGeneratedImages ? [{ key: "generated-images", label: "Generated Images" }] : []),
    ...(isParentProduct ? [{ key: "variants", label: `Variants${childProducts.length > 0 ? ` (${childProducts.length})` : ""}` }] : []),
  ];

  const getTabClassName = (isActive: boolean) =>
    isActive ? "tab-panel active" : "tab-panel";

  // Auto-save: debounced save on every change
  const updateField = (updates: Partial<Product>) => {
    setEditData(prev => {
      const next = { ...prev, ...updates };
      // Debounced auto-save
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => {
        if (onSave) onSave(next);
      }, 800);
      return next;
    });
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setEditData(prev => {
        if ((prev?.sellingFeatures?.length || 0) >= 5) return prev;
        const updated = { ...prev, sellingFeatures: [...(prev.sellingFeatures || []), newFeature.trim()] };
        if (onSave) onSave(updated);
        return updated;
      });
      setNewFeature("");
    }
  };

  const handleDeleteFeature = (idx: number) => {
    setEditData(prev => {
      const updated = { ...prev, sellingFeatures: (prev?.sellingFeatures || []).filter((_, i) => i !== idx) };
      if (onSave) onSave(updated);
      return updated;
    });
  };

  const handleUpdateFeature = (idx: number, value: string) => {
    setEditData(prev => {
      const features = [...(prev.sellingFeatures || [])];
      features[idx] = value;
      const updated = { ...prev, sellingFeatures: features };
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => { if (onSave) onSave(updated); }, 800);
      return updated;
    });
  };

  // Compress image to fit within maxDim and target JPEG quality
  const compressImage = (dataUrl: string, maxDim: number, quality = 0.7): Promise<string> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxDim || height > maxDim) {
          const scale = maxDim / Math.max(width, height);
          width = Math.round(width * scale);
          height = Math.round(height * scale);
        }
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;
        canvas.getContext("2d")?.drawImage(img, 0, 0, width, height);
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      img.onerror = () => resolve(dataUrl); // fallback to original on error
      img.src = dataUrl;
    });
  };

  const handleHeroImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setHeroLoading(true);
      const reader = new FileReader();
      reader.onload = async (event) => {
        const compressed = await compressImage(event.target?.result as string, 800, 0.75);
        const pid = editData?.id || editData?.itemNumber || "unknown";
        const imgUrl = await uploadToStorage(compressed, "products", `${pid}_hero_${Date.now()}.jpg`);
        setEditData(prev => {
          const updated = { ...prev, heroImage: imgUrl };
          if (onSave) onSave(updated);
          return updated;
        });
        setHeroLoading(false);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDetailImageUpload = (idx: number, e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDetailLoading(prev => ({ ...prev, [idx]: true }));
      setDetailSaveStatus("saving");
      const reader = new FileReader();
      reader.onload = async (event) => {
        const compressed = await compressImage(event.target?.result as string, 400, 0.5);
        const pid = editData?.id || editData?.itemNumber || "unknown";
        const imgUrl = await uploadToStorage(compressed, "products", `${pid}_detail${idx}_${Date.now()}.jpg`);
        log("Detail photo uploaded:", imgUrl.startsWith("http") ? "Storage URL" : "base64 fallback");
        // Build updated product from current editData ref
        const updatedProduct = await new Promise<Product>(resolve => {
          setEditData(prev => {
            const newDetailPhotos = [...(prev.detailPhotos || [])];
            newDetailPhotos[idx] = imgUrl;
            const updated = { ...prev, detailPhotos: newDetailPhotos };
            resolve(updated);
            return updated;
          });
        });
        if (onSave && updatedProduct) {
          try {
            log("Saving detail photo to database...");
            await onSave(updatedProduct);
            log("Detail photo saved successfully");
            setDetailSaveStatus("saved");
          } catch (err) {
            console.error("Detail photo save failed:", err);
            setDetailSaveStatus("error");
          }
        }
        setDetailLoading(prev => ({ ...prev, [idx]: false }));
        setTimeout(() => setDetailSaveStatus(null), 3000);
      };
      reader.readAsDataURL(file);
    }
  };

  const currentData = editData;
  const editing = true;

  return (
    <div className="rounded my-1 border" onKeyDown={e => e.stopPropagation()}>
      {/* Tab bar with delete button */}
      <div className="flex-between tab-bar-gradient tab-bar-top">
        <div className="flex">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setActiveTab(t.key)} className={getTabClassName(activeTab === t.key)}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3 pb-5">
          {!locked && onDelete && (
            <button onClick={() => {
              if (confirm("Delete this product? This cannot be undone.")) {
                onDelete(String(product.id!));
              }
            }} className="rounded ff-secondary fw-600 pointer bg-none text-md border-rose pad-chip-tag text-rose">Delete</button>
          )}
        </div>
      </div>
      <div className="bg-card panel-card-body">
      {/* Editable Item Number & Title */}
      <div className="flex gap-4 mb-5 items-start">
        {locked && (
          <div className="flex items-center gap-2 px-3 py-1 rounded bg-gray-8">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-mid">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0110 0v4" />
            </svg>
            <span className="ff-secondary fw-600 text-mid text-md">Locked</span>
          </div>
        )}
        <div className="col-120">
          <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Item #</div>
          {locked ? (
            <div className="fw-600 ff-secondary text-slate text-base pad-cell-sm">
              {currentData.itemNumber || "—"}
            </div>
          ) : (
            <textarea
              rows={1}
              value={currentData.itemNumber || ""}
              onChange={(e) => updateField({ itemNumber: e.target.value })}
              placeholder="e.g. UB-4402"
              className="w-full rounded fw-600 ff-secondary text-slate bg-transparent box-border outline-none text-base border pad-cell-sm"
            />
          )}
        </div>
        <div className="flex-1">
          <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Product Title</div>
          {locked ? (
            <div className="fw-600 ff-secondary text-dark text-lg pad-cell-sm">
              {currentData.title || "—"}
            </div>
          ) : (
            <textarea
              rows={1}
              value={currentData.title || ""}
              onChange={(e) => updateField({ title: e.target.value })}
              placeholder="Enter product title"
              className="w-full rounded fw-600 ff-secondary text-dark bg-transparent box-border outline-none text-lg border pad-cell-sm"
            />
          )}
        </div>
      </div>

      {/* Media tab */}
      {activeTab === "media" && (<>
        {!locked && (
        <div>
          <div className="media-upload-grid">
            <div className="ff-primary fw-700 uppercase ls-wide text-mid text-xs">Hero Image</div>
            <div className="ff-primary fw-700 uppercase ls-wide text-mid text-xs media-label-detail">Detail Photos</div>
            <label
              className={`image-upload-label aspect-square ${heroDragOver ? 'image-upload-drag-active' : 'image-upload-no-drag'}`}
              onDragOver={e => { e.preventDefault(); setHeroDragOver(true); }}
              onDragLeave={() => setHeroDragOver(false)}
              onMouseMove={e => setHeroZone(getZone(e))}
              onMouseLeave={() => setHeroZone(null)}
              onDrop={e => {
                e.preventDefault();
                setHeroDragOver(false);
                const file = e.dataTransfer.files?.[0];
                if (file && file.type.startsWith("image/")) {
                  const dt = new DataTransfer();
                  dt.items.add(file);
                  if (heroImageRef.current) { heroImageRef.current.files = dt.files; heroImageRef.current.dispatchEvent(new Event("change", { bubbles: true })); }
                }
              }}
            >
              {currentData?.heroImage && (
                <img src={currentData.heroImage} alt="" className={`absolute w-full h-full rounded object-cover left-0 top-0 image-hover-fade ${(heroZone || heroDragOver) ? 'opacity-30' : ''}`} />
              )}
              {heroLoading ? (
                <div className="flex-col items-center gap-2">
                  <div className="rounded-full animate-spin border-top-slate size-24 border-dashed-thick" />
                  <span className="text-xs text-light">Loading...</span>
                </div>
              ) : !currentData?.heroImage ? (
                <div className="flex flex-col items-center gap-1">
                  <span className="text-light text-3xl">+</span>
                  <span className="text-xs ff-secondary text-light">Drop or Click</span>
                </div>
              ) : (
                <>
                  <div className={`absolute flex flex-col items-center gap-1 image-hover-fade ${(heroZone === "center" || heroDragOver) ? 'opacity-100' : 'opacity-0'}`}>
                    <span className="text-dark text-2xl">{"⟳"}</span>
                    <span className="text-sm ff-secondary fw-600 text-dark">Change</span>
                  </div>
                  <span
                    onClick={e => { e.preventDefault(); e.stopPropagation(); setLightboxSrc(currentData.heroImage!); }}
                    className={`expand-close-icon absolute text-dark image-hover-fade bottom-6-right-6 ${heroZone === "corner" ? 'opacity-100' : 'opacity-0'}`}
                  >{"⤢"}</span>
                </>
              )}
              <input
                ref={heroImageRef}
                type="file"
                accept="image/*"
                onChange={handleHeroImageUpload}
                className="hidden"
              />
            </label>
            {[0, 1, 2, 3].map(i => {
              const photo = currentData?.detailPhotos?.[i];
              const isLoading = detailLoading[i];
              return (
                <label
                  key={i}
                  className={`image-upload-label aspect-square ${isLoading ? 'cursor-wait' : 'pointer'} ${detailDragOver === i ? 'image-upload-drag-active' : 'image-upload-no-drag'}`}
                  onDragOver={e => { e.preventDefault(); setDetailDragOver(i); }}
                  onDragLeave={() => setDetailDragOver(null)}
                  onMouseMove={e => setDetailZone({ i, zone: getZone(e) })}
                  onMouseLeave={() => setDetailZone(null)}
                  onDrop={e => {
                    e.preventDefault();
                    setDetailDragOver(null);
                    const file = e.dataTransfer.files?.[0];
                    if (file && file.type.startsWith("image/")) {
                      const dt = new DataTransfer();
                      dt.items.add(file);
                      const ref = detailImageRefs.current[i];
                      if (ref) { ref.files = dt.files; ref.dispatchEvent(new Event("change", { bubbles: true })); }
                    }
                  }}
                >
                  {photo && (
                    <img src={photo} className={`absolute w-full h-full rounded object-cover left-0 top-0 image-hover-fade ${(detailZone?.i === i || detailDragOver === i) ? 'opacity-30' : ''}`} />
                  )}
                  {isLoading ? (
                    <div className="rounded-full relative animate-spin border-top-slate z-1 size-20 border-200" />
                  ) : !photo ? (
                    <div className="flex flex-col items-center gap-1">
                      <span className="text-light text-3xl">+</span>
                      <span className="text-xs ff-secondary text-light">Drop or Click</span>
                    </div>
                  ) : (
                    <>
                      <div className={`absolute flex flex-col items-center gap-1 z-1 image-hover-fade ${(detailZone?.i === i && detailZone.zone === "center") || detailDragOver === i ? 'opacity-100' : 'opacity-0'}`}>
                        <span className="text-dark text-2xl">{"⟳"}</span>
                        <span className="text-sm ff-secondary fw-600 text-dark">Change</span>
                      </div>
                      <span
                        onClick={e => { e.preventDefault(); e.stopPropagation(); setLightboxSrc(photo); }}
                        className={`expand-close-icon absolute text-dark z-2 image-hover-fade bottom-6-right-6 ${detailZone?.i === i && detailZone.zone === "corner" ? 'opacity-100' : 'opacity-0'}`}
                      >{"⤢"}</span>
                    </>
                  )}
                  <input
                    ref={el => { if (el) detailImageRefs.current[i] = el; }}
                    type="file"
                    accept="image/*"
                    onChange={(e) => handleDetailImageUpload(i, e)}
                    className="hidden"
                  />
                </label>
              );
            })}
          </div>
          {detailSaveStatus && (
            <div className={`detail-save-status ${detailSaveStatus === 'saving' ? 'detail-save-saving' : detailSaveStatus === 'saved' ? 'detail-save-saved' : 'detail-save-error'}`}>
              {detailSaveStatus === "saving" && (<><div className="rounded-full animate-spin size-10 border-150 border-top-transparent" /> Saving to database...</>)}
              {detailSaveStatus === "saved" && (<><span className="text-md">{"✓"}</span> Saved</>)}
              {detailSaveStatus === "error" && (<><span className="text-md">{"✗"}</span> Save failed {"—"} photos kept locally</>)}
            </div>
          )}
        </div>
        )}
        {locked && currentData.heroImage && (
          <div className="flex gap-3">
            <div className="col-140">
              <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">Hero Image</div>
              <img src={currentData.heroImage} alt="Hero" className="rounded w-full h-auto" />
            </div>
          </div>
        )}

      </>)}

      {/* Product Info tab */}
      {activeTab === "info" && (
        <div className="flex-col gap-5">
          {/* Top row: Specifications & Positioning */}
          <div className="flex-start gap-3">
            {/* Specifications — left column */}
            <div className="card-section flex-1">
              <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">Specifications</div>
              <EditableFieldRow label="Category" value={currentData.category} onChange={(v) => updateField({ category: v })} editing={!locked} />
              <EditableFieldRow label="Dimensions" value={currentData.dimensions} onChange={(v) => updateField({ dimensions: v })} editing={!locked} />
              <EditableFieldRow label="Weight" value={currentData.weight} onChange={(v) => updateField({ weight: v })} editing={!locked} />
              <EditableFieldRow label="Material" value={currentData.material} onChange={(v) => updateField({ material: v })} editing={!locked} />
              <EditableFieldRow label="Color / Finish" value={currentData.color} onChange={(v) => updateField({ color: v })} editing={!locked} />
              <EditableFieldRow label="Pack Size" value={currentData.packSize} onChange={(v) => updateField({ packSize: v })} editing={!locked} />
              <EditableFieldRow label="What's Included" value={(currentData.whatsIncluded || []).join(', ')} onChange={(v) => updateField({ whatsIncluded: v.split(',').filter(s => s.trim()) })} editing={!locked} />
            </div>
            {/* Positioning — right column */}
            <div className="card-section flex-1">
              <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">Positioning</div>

              {/* Target Audience — text field + suggestion pills */}
              {(() => {
                const audienceList = (currentData.targetAudience || '').split(',').map((s: string) => s.trim()).filter(Boolean);
                const unusedSuggestions = TARGET_AUDIENCE_OPTIONS.filter(opt => !audienceList.includes(opt));
                return (
                  <div className="flex-start gap-5 py-2">
                    <div className="label-sm ls-wide col-130 pt-3">Target Audience</div>
                    <div className="flex-1">
                      {editing ? (
                        <>
                          <textarea
                            value={currentData.targetAudience || ''}
                            onChange={(e) => { updateField({ targetAudience: e.target.value }); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            placeholder="Type audiences separated by commas"
                            rows={1}
                            className="w-full rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 mb-2 resize-none tr-border"
                          />
                          {unusedSuggestions.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {unusedSuggestions.map(opt => (
                                <button
                                  key={opt}
                                  onClick={() => {
                                    const next = audienceList.length > 0
                                      ? currentData.targetAudience + ', ' + opt
                                      : opt;
                                    updateField({ targetAudience: next });
                                  }}
                                  className="no-border ff-secondary fw-500 pointer text-xs pad-chip-md rounded-full bg-transparent text-light border transition-150"
                                >{opt}</button>
                              ))}
                            </div>
                          )}
                        </>
                      ) : (
                        <div className={`text-base lh-normal flex-1 ${currentData.targetAudience ? 'text-dark' : 'text-light italic'}`}>
                          {currentData.targetAudience || 'Not set'}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              {/* Use Cases — auto-loaded from category */}
              {(() => {
                const categoryUseCase = CATEGORY_USE_CASES[currentData.category || ''] || '';
                const useCaseValue = typeof currentData.useCases === 'string'
                  ? currentData.useCases
                  : Array.isArray(currentData.useCases) ? (currentData.useCases as string[]).join(', ') : '';
                return (
                  <div className="flex-start gap-5 py-2">
                    <div className="label-sm ls-wide col-130 pt-3">Use Cases</div>
                    <div className="flex-1">
                      {categoryUseCase && !useCaseValue ? (
                        <div className="flex items-start gap-2">
                          <div className="flex-1 text-base ff-secondary lh-normal text-mid italic py-3 min-w-0">
                            {categoryUseCase}
                          </div>
                          <button
                            onClick={() => updateField({ useCases: categoryUseCase })}
                            className="btn-suggest nowrap"
                          >Use Suggested</button>
                        </div>
                      ) : (
                        <div className="flex items-start gap-2">
                          <textarea
                            value={useCaseValue}
                            onChange={(e) => { updateField({ useCases: e.target.value }); e.target.style.height = 'auto'; e.target.style.height = e.target.scrollHeight + 'px'; }}
                            ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                            placeholder={currentData.category ? (categoryUseCase || `No use cases mapped for "${currentData.category}" yet`) : "Select a category first"}
                            rows={1}
                            className="w-full rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border pre-wrap"
                          />
                          {categoryUseCase && useCaseValue && useCaseValue !== categoryUseCase && (
                            <button
                              onClick={() => updateField({ useCases: categoryUseCase })}
                              className="no-border ff-secondary fw-500 pointer text-xs pad-chip-tag rounded-full bg-transparent text-light border-dashed-default nowrap"
                              title="Reset to suggested use case"
                            >Reset</button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })()}

              <EditableFieldRow label="Differentiators" value={(currentData.differentiators || []).join(', ')} onChange={(v) => updateField({ differentiators: v.split(',').filter(s => s.trim()) })} editing={!locked} placeholder="What makes this unique?" />
            </div>
          </div>
          {/* Key Selling Features — full width below */}
          <div className="flex-start gap-3">
            <div className="card-section flex-1">
              <div className="flex items-center gap-3 mb-3">
                <span className="ff-primary fw-700 uppercase ls-wide text-mid text-xs">Key Selling Features</span>
                <span className={`text-xs fw-600 ff-secondary rounded pad-tight ${(currentData?.sellingFeatures || []).length >= 5 ? 'bg-slate-10 text-slate' : 'bg-rose-10 text-rose'}`}>
                  {(currentData?.sellingFeatures || []).length}/5
                </span>
              </div>
              {(currentData?.sellingFeatures || []).map((f, i) => (
                <div key={i} className="flex-start gap-5 py-2">
                  <div className="label-sm ls-wide col-130 pt-3">Feature {i + 1}</div>
                  <div className="flex items-start gap-2 flex-1">
                    <textarea
                      value={f}
                      onChange={(e) => { handleUpdateFeature(i, e.target.value); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                      ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                      rows={1}
                      className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border"
                    />
                    {editing && (
                      <button onClick={() => handleDeleteFeature(i)} className="no-border text-light pointer text-lg bg-none p-0-2 mt-1" aria-label="Remove feature">
                        {"×"}
                      </button>
                    )}
                  </div>
                </div>
              ))}
              {editing && (currentData?.sellingFeatures?.length || 0) < 5 && (
                <div className="flex-start gap-5 py-2">
                  <div className="label-sm ls-wide col-130"></div>
                  <div className="flex gap-2 flex-1">
                    <input
                      type="text"
                      value={newFeature}
                      onChange={(e) => setNewFeature(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") handleAddFeature(); }}
                      placeholder="Add new feature"
                      className="flex-1 rounded text-base ff-secondary text-dark bg-card outline-none pad-md border-slate-25"
                    />
                    <button onClick={handleAddFeature} className="bg-slate text-white no-border rounded ff-secondary text-sm fw-600 pointer pad-input-sm">
                      Add
                    </button>
                  </div>
                </div>
              )}
              {!editing && (currentData?.sellingFeatures?.length || 0) === 0 && (
                <div className="flex items-start gap-5 py-3">
                  <div className="label-sm ls-wide col-130 pt-1">Features</div>
                  <div className="text-base lh-normal flex-1 text-light italic">No features added</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Retailer Copy tabs */}
      {activeTab.startsWith("copy_") && (() => {
        const ch = activeTab.replace("copy_", "");
        const copy = product?.approvedCopy?.[ch];
        if (!copy) return null;
        const approvedDate = copy.approvedAt ? new Date(copy.approvedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "";
        return (
          <div className="flex-col gap-4">
            {approvedDate && (
              <div className="ff-secondary fw-600 text-sm text-peach">
                Approved {approvedDate}{copy.approvedBy ? ` by ${copy.approvedBy}` : ""}
              </div>
            )}
            <div className="card-section">
              <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Title</div>
              <div className="text-lg text-dark lh-relaxed">{copy.title || "—"}</div>
            </div>
            {(copy.bullets || []).length > 0 && (
              <div className="card-section">
                <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Bullet Points</div>
                {(copy.bullets || []).map((b: string, i: number) => (
                  <div key={i} className={`copy-bullet-row ${i >= (copy.bullets || []).length - 1 ? 'no-border' : ''}`}>
                    <span className="copy-bullet-number">{i + 1}.</span>{b}
                  </div>
                ))}
              </div>
            )}
            {copy.description && (
              <div className="card-section">
                <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Description</div>
                <div className="text-base text-dark lh-loose">{copy.description}</div>
              </div>
            )}
            {copy.backendKeywords && (
              <div className="card-section">
                <div className="ff-primary fw-700 uppercase ls-wide mb-1 text-mid text-xs">Backend Keywords</div>
                <div className="text-md text-mid bg-bg rounded lh-relaxed pad-base">{copy.backendKeywords}</div>
              </div>
            )}
          </div>
        );
      })()}

      {/* SEO Inputs tab */}
      {activeTab === "seo" && (
        <div className="flex gap-7">
          <div className="card-section flex-1">
            <div className="flex-between mb-3">
              <div className="ff-primary fw-700 uppercase ls-wide text-mid text-xs">Search Optimization</div>
              <button
                onClick={async () => {
                  setKeywordsGenerating(true);
                  try {
                    const res = await fetch('/api/generate-keywords', {
                      method: 'POST',
                      headers: { 'Content-Type': 'application/json' },
                      body: JSON.stringify({
                        title: currentData.title || product.title,
                        category: currentData.category,
                        material: currentData.material,
                        color: currentData.color,
                        sellingFeatures: currentData.sellingFeatures,
                        targetAudience: currentData.targetAudience,
                        competitorUrls: currentData.competitorRefs,
                      }),
                    });
                    if (res.ok) {
                      const data = await res.json();
                      if (data.keywords) updateField({ backendKeywords: data.keywords });
                    }
                  } catch { /* API not wired yet — no-op */ }
                  setKeywordsGenerating(false);
                }}
                disabled={keywordsGenerating}
                className={`no-border rounded ff-secondary text-xs fw-600 pointer flex items-center gap-2 pad-chip bg-slate-10 text-slate ${keywordsGenerating ? 'opacity-60' : ''}`}
              >
                {keywordsGenerating ? (
                  <><div className="rounded-full animate-spin size-10 border-150 border-top-slate" /> Generating...</>
                ) : (
                  <>{"✦"} Generate Keywords</>
                )}
              </button>
            </div>
            <div className="flex-start gap-5 py-2">
              <div className="label-sm ls-wide col-130 pt-3">Backend Keywords</div>
              <textarea
                value={currentData.backendKeywords || ""}
                onChange={(e) => { updateField({ backendKeywords: e.target.value }); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                placeholder="Enter backend search keywords or click Generate"
                rows={2}
                className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border pre-wrap"
              />
            </div>
          </div>
          <div className="card-section flex-1">
            <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">Competitive Intel</div>
            <div className="flex-start gap-5 py-2">
              <div className="label-sm ls-wide col-130 pt-3">Competitor URLs</div>
              <textarea
                value={currentData.competitorRefs || ""}
                onChange={(e) => { updateField({ competitorRefs: e.target.value }); e.target.style.height = "auto"; e.target.style.height = e.target.scrollHeight + "px"; }}
                ref={el => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } }}
                placeholder="Add competitor URLs for keyword gap analysis"
                rows={2}
                className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border pre-wrap"
              />
            </div>
          </div>
        </div>
      )}

      {/* Visual Direction tab */}
      {activeTab === "visual-direction" && currentData?.artDirection?.images && (
        <div className="flex-col gap-5">
          {currentData.artDirection.savedAt && (
            <div className="ff-secondary fw-600 text-sm text-mid">
              Saved {new Date(currentData.artDirection.savedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
          <div className="expanded-product-grid">
            {currentData.artDirection.images.map((img: Record<string, unknown>, idx: number) => {
              const updateImage = (field: string, value: string) => {
                const images = [...(currentData.artDirection?.images || [])];
                images[idx] = { ...images[idx], [field]: value };
                updateField({ artDirection: { ...currentData.artDirection, images } });
              };
              const autoSize = (el: HTMLTextAreaElement | null) => { if (el) { el.style.height = "auto"; el.style.height = el.scrollHeight + "px"; } };
              const imgType = ((img.type as string) || "image").toLowerCase();
              const isInfographic = imgType === "infographic";
              return (
                <div key={idx} className="bg-bg rounded border p-8">
                  <div className="flex-between mb-4">
                    <span className="ff-primary fw-700 uppercase ls-wide text-mid text-xs">
                      Slot {img.slot as string | number}
                    </span>
                    <Badge color="var(--mid)">{(img.type as string) || "image"}</Badge>
                  </div>
                  {/* Scene description — the core VD field for all image types */}
                  <div className="flex-start gap-5 mb-3 py-2">
                    <div className="label-sm ls-wide col-130 pt-3">Scene</div>
                    <textarea
                      value={(img.vd as string) || (img.visualDirection as string) || ""}
                      onChange={e => { updateImage("vd", e.target.value); autoSize(e.target); }}
                      ref={autoSize}
                      placeholder={imgType === "hero" ? "Describe the hero shot — angle, background, lighting" : imgType === "lifestyle" ? "Describe the lifestyle scene — setting, people, mood" : "Describe the visual layout and composition"}
                      rows={2}
                      className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border"
                    />
                  </div>
                  {/* Headline + Copy — only for infographics (text overlays) */}
                  {isInfographic && (
                    <>
                      <div className="flex-start gap-5 mb-3 py-2">
                        <div className="label-sm ls-wide col-130 pt-3">Headline</div>
                        <textarea
                          value={(img.headline as string) || (img.title as string) || ""}
                          onChange={e => { updateImage("headline", e.target.value); autoSize(e.target); }}
                          ref={autoSize}
                          placeholder="Infographic headline text"
                          rows={1}
                          className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border"
                        />
                      </div>
                      <div className="flex-start gap-5 py-2">
                        <div className="label-sm ls-wide col-130 pt-3">Copy</div>
                        <textarea
                          value={(img.copy as string) || ""}
                          onChange={e => { updateImage("copy", e.target.value); autoSize(e.target); }}
                          ref={autoSize}
                          placeholder="Infographic body copy"
                          rows={1}
                          className="flex-1 rounded text-base ff-secondary text-dark bg-card overflow-hidden lh-normal box-border outline-none pad-md border-slate-25 resize-none tr-border"
                        />
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Generated Images tab */}
      {activeTab === "generated-images" && (() => {
        // Merge approved images from DB with any cached generated images
        const dbSketch = approvedImages.filter(ai => (ai.image_style || "sketch") === "sketch");
        const dbRealism = approvedImages.filter(ai => ai.image_style === "realism");
        const cachedSketch = product?.artDirection?.generatedImages?.sketch || {};
        const cachedRealism = product?.artDirection?.generatedImages?.realism || {};

        const renderImageSet = (dbImages: ApprovedImage[], cachedImages: Record<string, any>) => {
          // Merge: DB images take priority, fill in from cache for any missing slots
          const allSlots = new Set<number>();
          dbImages.forEach(ai => allSlots.add(ai.slot));
          Object.keys(cachedImages).forEach(s => allSlots.add(Number(s)));
          const sortedSlots = [...allSlots].sort((a, b) => a - b);

          if (sortedSlots.length === 0) return (
            <div className="text-sm text-light ff-secondary py-5">
              No images generated for this style yet.
            </div>
          );

          return (
            <div className="grid-auto-160 gap-4">
              {sortedSlots.map(slot => {
                const dbImg = dbImages.find(ai => ai.slot === slot);
                const cachedImg = cachedImages[slot];
                const imgUrl = dbImg?.image_url || cachedImg?.url || cachedImg?.dataUrl;
                const imgType = dbImg?.image_type || cachedImg?.type || product?.artDirection?.images?.find((i: Record<string, unknown>) => i.slot === slot)?.type || "image";
                const imgTitle = product?.artDirection?.images?.find((i: Record<string, unknown>) => i.slot === slot)?.title || `Slot ${slot}`;
                const approvedDate = dbImg?.approved_at ? new Date(dbImg.approved_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : null;

                return (
                  <div key={slot} className={`rounded overflow-hidden bg-card ${dbImg ? 'border-slate-25' : 'border-150'}`}>
                    <div className="w-full relative bg-bg flex items-center justify-center aspect-square">
                      {imgUrl ? (
                        <img src={imgUrl} alt={imgTitle} className="w-full h-full object-cover pointer" onClick={() => setLightboxSrc(imgUrl)} />
                      ) : (
                        <div className="text-center text-light">
                          <span className="text-xl">{"◻"}</span>
                          <div className="text-xs ff-secondary mt-1">No image</div>
                        </div>
                      )}
                    </div>
                    <div className="pad-xs">
                      <div className="text-sm fw-600 ff-primary text-dark truncate mb-half">{imgTitle}</div>
                      <div className="flex-between">
                        <span className="text-xs text-light ff-secondary">
                          Slot {slot}{approvedDate ? ` — ${approvedDate}` : ""}
                        </span>
                        {imgUrl && (
                          <a href={imgUrl} download={`${product.itemNumber || product.id}_${imgType}_${slot}.png`} className="text-xs text-slate fw-600 text-decoration-none">{"↓"}</a>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        };

        const sketchImages = dbSketch;
        const realismImages = dbRealism;
        const hasSketch = sketchImages.length > 0 || Object.keys(cachedSketch).length > 0;
        const hasRealism = realismImages.length > 0 || Object.keys(cachedRealism).length > 0;

        return (
          <div className="flex-col gap-3">
            {hasSketch && (
              <div>
                <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">
                  Sketch <span className="fw-600 ff-secondary normal-case text-sm">{"—"} {sketchImages.length + Object.keys(cachedSketch).length} images</span>
                </div>
                {renderImageSet(sketchImages, cachedSketch)}
              </div>
            )}
            {hasRealism && (
              <div>
                <div className="ff-primary fw-700 uppercase ls-wide mb-3 text-mid text-xs">
                  Realism <span className="fw-600 ff-secondary normal-case text-sm">{"—"} {realismImages.length + Object.keys(cachedRealism).length} images</span>
                </div>
                {renderImageSet(realismImages, cachedRealism)}
              </div>
            )}
          </div>
        );
      })()}
      {/* Variants tab — manage child products */}
      {activeTab === "variants" && isParentProduct && (
        <VariantsTabContent
          parentProduct={product}
          childProducts={childProducts}
          onSave={onSave!}
          onDelete={onDelete!}
          locked={locked}
        />
      )}
      {/* ─── LIGHTBOX ─── */}
      {lightboxSrc && (
        <div
          onClick={() => setLightboxSrc(null)}
          className="lightbox-overlay"
        >
          <img
            src={lightboxSrc}
            alt=""
            onClick={e => e.stopPropagation()}
            className="lightbox-image"
          />
          <span
            onClick={() => setLightboxSrc(null)}
            className="lightbox-close"
          >{"✕"}</span>
        </div>
      )}
      </div>
    </div>
  );
});
